import {
  cleanTwoLineAddressWithoutState,
  PropertyAddress,
} from '@urbanx/agx-ui-components'
import { AgencyAgentModel, Name } from '@urbanx/agx.agencies.client'
import {
  CampaignAdminModel,
  CampaignAdminModelMarketingStatus,
  CampaignAdminModelStage,
} from '@urbanx/agx.campaigns.client'
import { cleanFullAddress } from '@urbanx/agx.ui-components2'
import dayjs, { Dayjs } from 'dayjs'
import { LaunchFilterOptions, SearchableCampaign } from 'types/campaigns'
import { splitPascalCase } from './stringUtils'

const aggregateNamePieces = (name?: Name): string | undefined => {
  const namePieces = [name?.firstName, name?.lastName].filter(
    (namePiece) => namePiece !== undefined && namePiece !== ''
  )

  if (namePieces.length === 0) return undefined

  return namePieces.join(' ')
}

export const formatCampaignStageLabel = (
  stage: CampaignAdminModelStage
): string => {
  const [stageProcess, ...stageStatus] = splitPascalCase(stage).split(' ')
  return `${stageProcess} • ${stageStatus.join(' ')}`
}

export const createAgentsNameLookup = (
  agents: AgencyAgentModel[]
): Record<string, string> =>
  agents
    .map((agent) => {
      const agentName = aggregateNamePieces(agent.name)
      return {
        ...agent,
        fullName: agentName,
      }
    })
    .filter((agent) => agent.fullName !== undefined && agent.id !== undefined)
    .reduce(
      (lookup, agent) => {
        lookup[agent.id!] = agent.fullName as string
        return lookup
      },
      {} as Record<string, string>
    )

export const CreateSearchableCampaigns = (
  campaigns: CampaignAdminModel[],
  agentsNameLookup: Record<string, string>
): SearchableCampaign[] => {
  return campaigns.map((campaign) => {
    const fullAddressSearchable = cleanFullAddress(
      campaign.address
    )?.toLocaleLowerCase()
    const cleanAddress = cleanTwoLineAddressWithoutState(
      campaign.address as PropertyAddress
    )

    return {
      ...campaign,
      cleanAddress: cleanAddress,
      leadAgentName: !campaign.leadAgentId
        ? '--'
        : (agentsNameLookup[campaign.leadAgentId] ?? '--'),
      secondAgentName: !campaign.secondAgentId
        ? '--'
        : (agentsNameLookup[campaign.secondAgentId] ?? '--'),
      fullAddressSearchable: fullAddressSearchable ?? '',
      filterableLaunchDate: campaign.launchDate
        ? dayjs(campaign.launchDate)
        : undefined,
    }
  })
}

const getLaunchDatePriority = (
  today: Dayjs,
  launchDate: Dayjs | undefined
): number => {
  if (!launchDate) return 2
  if (launchDate.isSame(today, 'day')) return 0
  if (launchDate.isAfter(today, 'day')) return 1
  return 3
}

const sortCampaigns = (
  campaigns: SearchableCampaign[]
): SearchableCampaign[] => {
  const today = dayjs()

  return campaigns.sort((a, b) => {
    const aPriority = getLaunchDatePriority(today, a.filterableLaunchDate)
    const bPriority = getLaunchDatePriority(today, b.filterableLaunchDate)

    if (aPriority !== bPriority) return aPriority - bPriority

    if (!a.filterableLaunchDate || !b.filterableLaunchDate) return 0

    // Past launches appear last, with most recent past launch first.
    if (aPriority === 3) {
      return b.filterableLaunchDate.valueOf() - a.filterableLaunchDate.valueOf()
    }

    return a.filterableLaunchDate.valueOf() - b.filterableLaunchDate.valueOf()
  })
}

export const filterCampaigns = (
  data: SearchableCampaign[] | undefined,
  searchText?: string,
  statusFilter?: CampaignAdminModelStage[],
  agentsFilter?: string[],
  marketingFilter?: CampaignAdminModelMarketingStatus[],
  contractFilter?: string[],
  launchFilter?: LaunchFilterOptions
): SearchableCampaign[] => {
  if (!data) return []

  const isFiltered =
    searchText ||
    statusFilter ||
    agentsFilter ||
    marketingFilter ||
    contractFilter ||
    launchFilter

  let filteredData = [...data]

  if (isFiltered) {
    if (searchText) {
      filteredData = filteredData.filter((campaign) =>
        campaign.fullAddressSearchable.includes(searchText.toLocaleLowerCase())
      )
    }

    if (statusFilter) {
      filteredData = filteredData.filter(
        (campaign) => campaign.stage && statusFilter.includes(campaign.stage)
      )
    }

    if (agentsFilter) {
      filteredData = filteredData.filter(
        (campaign) =>
          (campaign.leadAgentId &&
            agentsFilter.includes(campaign.leadAgentId)) ||
          (campaign.secondAgentId &&
            agentsFilter.includes(campaign.secondAgentId)) ||
          false
      )
    }

    if (marketingFilter) {
      filteredData = filteredData.filter(
        (campaign) =>
          campaign.marketingStatus &&
          marketingFilter.includes(campaign.marketingStatus)
      )
    }

    if (contractFilter) {
      filteredData = filteredData.filter(
        (campaign) =>
          (campaign.blankContractIsLive
            ? contractFilter.includes('Live')
            : false) ||
          (!campaign.blankContractIsLive
            ? contractFilter.includes('Offline')
            : false)
      )
    }

    if (launchFilter) {
      filteredData = filteredData.filter((campaign) => {
        if (!campaign.filterableLaunchDate) return false

        switch (launchFilter) {
          case LaunchFilterOptions.Today:
            return campaign.filterableLaunchDate.isSame(dayjs(), 'day')
          case LaunchFilterOptions.ThisWeek:
            return campaign.filterableLaunchDate.isSame(dayjs(), 'week')
          case LaunchFilterOptions.NextWeek:
            return campaign.filterableLaunchDate.isSame(
              dayjs().add(1, 'week'),
              'week'
            )
        }
      })
    }
  }

  return sortCampaigns(filteredData)
}
